import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './SideNavbar.css';
import { useAuth } from '../AuthProvider';
import PERMISSIONS from '../../permissions';
import NavItem from './NavItem';
import { FaCar, FaUser, FaBuilding, FaFileInvoice, FaGasPump, FaMoneyBill, FaHome, FaTags, FaChevronDown, FaChevronRight } from "react-icons/fa"; 
import { FaSignOutAlt } from "react-icons/fa";
import { FaCarSide } from "react-icons/fa";
import { FaWarehouse } from "react-icons/fa";
import GenericDropdown from './GenericDropdown';

const SideNavbar = () => {
  const {vendorDetails, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeItem, setActiveItem] = useState('');
  const [dropdownState, setDropdownState] = useState({
    isExpanded: false,
    items: [],
    position: { left: 0, top: 0 }
  });
  const menuRefs = useRef({});
  const sidebarRef = useRef(null);

  useEffect(() => {
    if (window.location.pathname === '/login') {
      setActiveItem('home');
    } else {
      setActiveItem(window.location.pathname.split('/')[2]);
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      if (dropdownState.isExpanded) {
        setDropdownState({ ...dropdownState, isExpanded: false });
      }
    };

    const sidebarElement = sidebarRef.current;
    if (sidebarElement) {
      sidebarElement.addEventListener('scroll', handleScroll);
      return () => {
        sidebarElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [dropdownState]);

  const handleClick = (path, event, items) => {
    setActiveItem(path);
    
    if (items && items.length > 0) {
      const rect = event.currentTarget.getBoundingClientRect();
      setDropdownState({
        isExpanded: true,
        items,
        position: {
          left: rect.right + 10,
          top: rect.top
        }
      });
    } else {
      setDropdownState({ ...dropdownState, isExpanded: false });
      navigate(path);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { 
      permission: PERMISSIONS.VENDOR_TRIP_READ, 
      label: 'Trips', 
      path: 'trips', 
      icon: <FaCar />,
      items: [
        { label: 'All Trips', path: 'trips/all', permission: PERMISSIONS.VENDOR_TRIP_READ },
        { label: 'Trip Types', path: 'trip_types', permission: PERMISSIONS.VENDOR_TRIP_TYPE_READ },
        { label: 'Completed', path: 'trips/completed', permission: PERMISSIONS.VENDOR_TRIP_READ },
        { label: 'Add New Trip', path: 'trips/add-new', permission: PERMISSIONS.VENDOR_TRIP_CREATE },
        { label: 'Add Completed Trip', path: 'trips/add-completed', permission: PERMISSIONS.VENDOR_TRIP_CREATE },
      ]
    },
    {
      permission: PERMISSIONS.VENDOR_ALL_CAR_READ,
      label: 'Cars',
      path: 'cars',
      icon: <FaCar />,
      items: [
        { label: 'All Cars', path: 'cars/all', permission: PERMISSIONS.VENDOR_ALL_CAR_READ },
        { label: 'Car Types', path: 'cars/car_types', permission: PERMISSIONS.VENDOR_CAR_TYPE_READ },
        { label: 'Fuel Analysis', path: 'cars/fuel_analysis', permission: PERMISSIONS.VENDOR_FUEL_ANALYSIS_READ },
      ]
    },
    { permission: PERMISSIONS.VENDOR_DRIVER_READ, label: 'Drivers', path: 'drivers', icon: <FaUser /> },
    { permission: PERMISSIONS.VENDOR_COMPANY_READ, label: 'Companies', path: 'companies', icon: <FaBuilding /> },
    { permission: PERMISSIONS.VENDOR_LABEL_READ, label: 'Labels', path: 'labels', icon: <FaTags /> },
    { permission: PERMISSIONS.VENDOR_TAX_TYPE_READ, label: 'Taxes', path: 'taxes', icon: <FaMoneyBill /> },
    { permission: PERMISSIONS.VENDOR_BILLING_ITEM_READ, label: 'Billing Items', path: 'billing_items', icon: <FaFileInvoice /> },
    { permission: PERMISSIONS.VENDOR_GARAGE_READ, label: 'Garages', path: 'garages', icon: <FaWarehouse /> },
    {
      permission: PERMISSIONS.VENDOR_FUEL_TRANSACTION_READ,
      label: 'Fuels',
      path: 'fuels',
      icon: <FaGasPump />,
      items: [
        { label: 'Fuel Entries', path: 'fuels', permission: PERMISSIONS.VENDOR_FUEL_TRANSACTION_READ },
        { label: 'Fuel Stations', path: 'fuels/fuel_stations', permission: PERMISSIONS.VENDOR_FUEL_STATION_READ },
      ]
    },
    { permission: PERMISSIONS.VENDOR_EXPENDITURE_READ, label: 'Expenditures', path: 'expenditures', icon: <FaMoneyBill /> },
    { permission: PERMISSIONS.VENDOR_PETTY_CASH_READ, label: 'Petty Cash', path: 'pettycash', icon: <FaMoneyBill /> },
    { permission: PERMISSIONS.VENDOR_INVOICE_READ, label: 'Invoices', path: 'invoices', icon: <FaFileInvoice /> },
  ];

  return (
    <>
      <div className='sideNavbar' ref={sidebarRef}>
        <ul className='sideNavbar-menu'>
          <NavItem
            permission={null}
            label="Home"
            path="home"
            activeItem={activeItem}
            handleClick={(path, e) => handleClick(path, e)}
            icon={<FaHome />}
          />
          <hr className="sideNavbar-divider" />
          {menuItems.map((item, index) => (
            <React.Fragment key={item.path}>
              <NavItem
                permission={item.permission}
                label={item.label}
                path={item.path}
                activeItem={activeItem}
                handleClick={(path, e) => handleClick(path, e, item.items)}
                icon={item.icon}
                hasDropdown={!!item.items}
                isDropdownOpen={dropdownState.isExpanded && dropdownState.items === item.items}
              />
              {(item.path === 'trip_types' || index === menuItems.length - 1) && (
                <hr className="sideNavbar-divider" />
              )}
            </React.Fragment>
          ))}
          <NavItem
            permission={null}
            label="Logout"
            path="logout"
            activeItem={activeItem}
            handleClick={handleLogout}
            icon={<FaSignOutAlt />}
          />
        </ul>
      </div>
      <GenericDropdown
        isExpanded={dropdownState.isExpanded}
        onClose={() => setDropdownState({ ...dropdownState, isExpanded: false })}
        items={dropdownState.items}
        position={dropdownState.position}
      />
    </>
  );
};

export default SideNavbar;